'use client'

import accountConfig from '@/feature/account/config/account.json'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Link } from '@/ui/links/link/Link.client'
import { useId } from 'react'
import type { PrivateUiInputProps, PublicInputProps } from '../../_shared/AbstractInput.client'
import { AbstractInput } from '../../_shared/AbstractInput.client'
import { Checkbox } from './Checkbox.client'

type TermsCheckboxProps = PrivateUiInputProps<boolean, object>

function TermsCheckboxFields({
  label,
  name,
  error,
  value,
  onChange,
  isDisabled,
  isRequired,
  isLoading,
}: TermsCheckboxProps) {
  const inputId = useId()
  const { t } = useTranslation(['account'])

  const hasError = Boolean(error?.message)
  const shouldDisplayError = !isLoading && !isDisabled && hasError
  const errorId = shouldDisplayError ? `${inputId}-error` : false

  return (
    <Checkbox
      id={inputId}
      name={name}
      isSelected={value}
      onChange={onChange}
      isDisabled={isDisabled}
      isInvalid={shouldDisplayError}
      isRequired={isRequired}
      errorId={errorId}
      shouldDisplayError={shouldDisplayError}
      errorMessage={error?.message}
    >
      {label}{' '}
      <Link
        bold
        href={accountConfig.account.terms}
        opensInNewTab
        className="inline [&>span]:text-wrap"
      >
        {t('account:create.doa.fields.terms.link')}
      </Link>
    </Checkbox>
  )
}

type CheckboxInputProps = PublicInputProps<object, 'required'>

/**
 * @example
 * ```
 * <TermsCheckboxInput name="acceptTerms" label="I agree to the terms and conditions" required />
 * ```
 */
export function TermsCheckboxInput({ name, label, ...rules }: CheckboxInputProps) {
  return (
    <AbstractInput
      {...rules}
      name={name}
      render={({ field, fieldState, formState }) => (
        <TermsCheckboxFields
          {...field}
          name={name}
          label={label}
          error={fieldState.error}
          isLoading={false} // No need to validate asynchronously for checkboxes
          isDisabled={field.disabled}
          isRequired={fieldState.isRequired}
          isSubmitting={formState.isSubmitting}
        />
      )}
    />
  )
}
